// Shared time-axis policy for custom-drawn chart axes.
//
// Owns ET formatting, cadence selection, and collision suppression so every
// custom axis reads the same. Renderers whose public API owns tick placement
// (Lightweight Charts' built-in scale, TradingView) keep their native layout
// but consume the formatting and interval semantics from here.

export const ET_TIME_ZONE = 'America/New_York';
export const SESSION_OPEN_MINUTE = 570; // 09:30 ET

// Minute- and hour-aligned steps only: arbitrary cadences produce labels that
// drift under panning and read as noise next to a price axis.
const CADENCE_STEPS_SECONDS = [
  60, 120, 300, 600, 900, 1800, 3600, 7200, 14400, 21600, 43200, 86400,
];

export const DEFAULT_LABEL_WIDTH_PX = 44;
const LABEL_GAP_PX = 10;
const LAST_LABEL_GAP_PX = 6;

const LABEL_KIND = Object.freeze({
  SESSION_OPEN: 'session-open',
  HOUR: 'hour',
  CADENCE: 'cadence',
  LAST: 'last',
});

// Session open orients the whole view, so it outranks the cadence grid. The
// final column ranks lowest: it is useful but must never displace an aligned
// tick.
const KIND_PRIORITY = Object.freeze({
  [LABEL_KIND.SESSION_OPEN]: 3,
  [LABEL_KIND.HOUR]: 2,
  [LABEL_KIND.CADENCE]: 1,
  [LABEL_KIND.LAST]: 0,
});

const etTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: ET_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const etPartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: ET_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

export function formatEtTime(epochMs) {
  if (!Number.isFinite(epochMs)) return '';
  return etTimeFormatter.format(new Date(epochMs));
}

export function formatEtChartTime(epochSeconds) {
  return Number.isFinite(epochSeconds) ? formatEtTime(epochSeconds * 1000) : '';
}

export function etSecondOfDay(epochMs) {
  if (!Number.isFinite(epochMs)) return null;
  const parts = etPartsFormatter.formatToParts(new Date(epochMs));
  const value = (type) => Number(parts.find((part) => part.type === type)?.value);
  return value('hour') * 3600 + value('minute') * 60 + value('second');
}

export function isSessionOpen(epochMs) {
  return etSecondOfDay(epochMs) === SESSION_OPEN_MINUTE * 60;
}

/**
 * Pick the finest minute/hour-aligned cadence whose label count fits the plot.
 */
export function chooseLabelCadenceSeconds({
  visibleDurationSeconds,
  plotWidthPx,
  labelWidthPx = DEFAULT_LABEL_WIDTH_PX,
}) {
  if (!Number.isFinite(visibleDurationSeconds) || visibleDurationSeconds <= 0) return null;
  if (!Number.isFinite(plotWidthPx) || plotWidthPx <= 0) return null;

  const slotWidth = Math.max(1, labelWidthPx + LABEL_GAP_PX);
  const maxLabels = Math.max(1, Math.floor(plotWidthPx / slotWidth));

  for (const step of CADENCE_STEPS_SECONDS) {
    if (visibleDurationSeconds / step <= maxLabels) return step;
  }
  return CADENCE_STEPS_SECONDS[CADENCE_STEPS_SECONDS.length - 1];
}

function labelKind(epochMs, cadenceSeconds) {
  const secondOfDay = etSecondOfDay(epochMs);
  if (secondOfDay == null) return null;
  if (secondOfDay === SESSION_OPEN_MINUTE * 60) return LABEL_KIND.SESSION_OPEN;
  if (secondOfDay % 3600 === 0) return LABEL_KIND.HOUR;
  if (cadenceSeconds && secondOfDay % cadenceSeconds === 0) return LABEL_KIND.CADENCE;
  return null;
}

/**
 * Interpolate a plot x for an arbitrary time between projected columns.
 *
 * The renderer's time scale is index-based, so equal time spans occupy unequal
 * pixel spans wherever column density varies. Interpolating between the
 * bracketing columns keeps a cadence tick at its true position instead of
 * requiring a column to exist at exactly that instant.
 */
function interpolateX(columns, t) {
  if (t < columns[0].t || t > columns[columns.length - 1].t) return null;

  let low = 0;
  let high = columns.length - 1;
  while (high - low > 1) {
    const mid = (low + high) >> 1;
    if (columns[mid].t <= t) low = mid;
    else high = mid;
  }

  const left = columns[low];
  const right = columns[high];
  if (t === left.t) return left.x;
  const span = right.t - left.t;
  if (span <= 0) return left.x;
  return left.x + ((t - left.t) / span) * (right.x - left.x);
}

/**
 * Choose which times get a label on a custom axis.
 *
 * `columns` are the renderer's already-projected points, ascending in x:
 * `[{ t: epochMs, x: px }]`. Labels are placed at clean cadence boundaries
 * whose positions are interpolated between columns, so a gap in column coverage
 * no longer silently drops the label that belongs there. The first visible
 * column is deliberately not pinned -- an arbitrary pan boundary churns on every
 * drag and crowds out the aligned ticks that make the axis readable. The
 * crosshair still reports the exact time under the cursor.
 */
export function selectTimeAxisLabels({
  columns,
  plotWidthPx,
  labelWidthPx = DEFAULT_LABEL_WIDTH_PX,
  includeLast = true,
}) {
  if (!Array.isArray(columns) || !columns.length) return [];

  const usable = columns
    .filter((column) => Number.isFinite(column?.t) && Number.isFinite(column?.x))
    .sort((a, b) => a.t - b.t);
  if (!usable.length) return [];

  const firstT = usable[0].t;
  const lastColumn = usable[usable.length - 1];
  const visibleDurationSeconds = (lastColumn.t - firstT) / 1000;
  const cadenceSeconds = chooseLabelCadenceSeconds({
    visibleDurationSeconds,
    plotWidthPx,
    labelWidthPx,
  });

  const minGapPx = labelWidthPx + LABEL_GAP_PX;
  const lastGapPx = labelWidthPx + LAST_LABEL_GAP_PX;

  const candidates = [];
  if (cadenceSeconds) {
    // Step in ET wall-clock terms so ticks land on clean local boundaries.
    const startSecond = etSecondOfDay(firstT);
    if (startSecond != null) {
      const toFirstTick = (cadenceSeconds - (startSecond % cadenceSeconds)) % cadenceSeconds;
      const stepMs = cadenceSeconds * 1000;
      for (let t = firstT + toFirstTick * 1000; t <= lastColumn.t; t += stepMs) {
        const kind = labelKind(t, cadenceSeconds);
        if (!kind) continue;
        const x = interpolateX(usable, t);
        if (!Number.isFinite(x)) continue;
        candidates.push({ t, x, kind, priority: KIND_PRIORITY[kind] });
      }
    }
  }
  if (includeLast && !candidates.some((candidate) => candidate.t === lastColumn.t)) {
    candidates.push({
      t: lastColumn.t,
      x: lastColumn.x,
      kind: LABEL_KIND.LAST,
      priority: KIND_PRIORITY[LABEL_KIND.LAST],
    });
  }
  if (!candidates.length) return [];

  // Highest priority claims space first, so a session-open or hour label is
  // never dropped in favour of a neighbouring minute tick.
  const ordered = candidates
    .slice()
    .sort((a, b) => (b.priority - a.priority) || (a.x - b.x));

  const placed = [];
  for (const candidate of ordered) {
    const requiredGap = candidate.kind === LABEL_KIND.LAST ? lastGapPx : minGapPx;
    const collides = placed.some((entry) => {
      const gap = Math.min(
        requiredGap,
        entry.kind === LABEL_KIND.LAST ? lastGapPx : minGapPx
      );
      return Math.abs(entry.x - candidate.x) < gap;
    });
    if (!collides) placed.push(candidate);
  }

  return placed.sort((a, b) => a.x - b.x);
}
